import React from 'react';

export interface MenuItemSSR {
  /** Display label for the menu item */
  label: string;
  /** Optional URL for the menu item */
  url?: string;
}

export interface MegaMenuV2SkeletonSSRProps {
  /** Menu items from SDM header configuration to display actual labels */
  menuItems?: MenuItemSSR[];
}

// Default placeholder count when no menu items provided
const DEFAULT_PLACEHOLDER_COUNT = 5;

/**
 * SSR-safe MegaMenu skeleton component.
 * Uses pure inline styles for SSR compatibility (no Chakra UI).
 * Shows skeleton placeholders on mobile, actual menu labels on desktop.
 */
export const MegaMenuV2SkeletonSSR = ({ menuItems }: MegaMenuV2SkeletonSSRProps) => {
  const hasMenuItems = menuItems && menuItems.length > 0;
  const placeholderCount = hasMenuItems ? menuItems.length : DEFAULT_PLACEHOLDER_COUNT;

  return (
    <nav
      data-testid="mega-menu-v2-skeleton-ssr"
      aria-label={hasMenuItems ? 'Main navigation' : 'Main navigation loading'}
      aria-busy={!hasMenuItems}
      style={{
        borderBottom: '1px solid #E2E8F0',
        position: 'relative',
        paddingLeft: '2.672rem',
        paddingRight: '2.672rem',
      }}
    >
      {/* CSS for responsive display - skeletons on mobile, labels on desktop */}
      <style>{`
        .mega-menu-ssr-skeleton { display: flex; }
        .mega-menu-ssr-labels { display: none; }
        @media (min-width: 1024px) {
          .mega-menu-ssr-skeleton { display: none; }
          .mega-menu-ssr-labels { display: flex; }
        }
      `}</style>

      {/* Skeleton placeholders - visible on mobile */}
      <ul
        className="mega-menu-ssr-skeleton"
        style={{
          gap: '1.67rem',
          maxWidth: '1440px',
          margin: '0 auto',
          padding: 0,
          listStyle: 'none',
        }}
      >
        {Array.from({ length: placeholderCount }).map((_, index) => (
          <li
            key={`skeleton-menu-item-${index}`}
            aria-hidden="true"
            style={{
              height: '40px',
              width: '100px',
              backgroundColor: '#EDF2F7',
              borderRadius: '2px',
            }}
          />
        ))}
      </ul>

      {/* Actual menu labels - visible on desktop when data available */}
      {hasMenuItems && (
        <ul
          className="mega-menu-ssr-labels"
          style={{
            gap: '1.67rem',
            maxWidth: '1440px',
            margin: '0 auto',
            padding: 0,
            listStyle: 'none',
          }}
        >
          {menuItems.map((item, index) => (
            <li
              key={`menu-item-${index}`}
              style={{
                listStyle: 'none',
              }}
            >
              {/* Non-interactive placeholder, replaced by interactive Button on hydration */}
              <span
                style={{
                  color: '#1A202C',
                  fontFamily: 'GT Eesti Text, sans-serif',
                  fontSize: '1.166rem',
                  fontWeight: 300,
                  lineHeight: '150%',
                  letterSpacing: '0.01em',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textAlign: 'left',
                  position: 'relative',
                  border: 0,
                  borderBottom: '2px solid transparent',
                  minHeight: '40px',
                  cursor: 'default',
                }}
              >
                <span>{item.label}</span>
                {/* Expand icon - matches menuDefaultIcon size (2rem = 32px) */}
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
                </svg>
              </span>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
};

MegaMenuV2SkeletonSSR.displayName = 'MegaMenuV2SkeletonSSR';
